import {
  type CreatePurchaseOrderDTO,
  Currency,
  DirtLevelEnum,
  PricingTypeEnum,
  useApiAccountsV1UserRetrieve,
  WhoFixesDateEnum,
} from 'api';
import { dayjs } from 'date';
import { Form, type InferType, useForm, yupResolver } from 'form';
import { Box, Button, Page, Section } from 'ui';

import { PurchaseOrderFormSections } from '../../components';
import { FormContextProvider } from '../../contexts/PurchaseOrderFormContext';
import { useCreatePurchaseOrder } from '../../hooks/';
import { createPurchaseOrderSchema } from '../../schemas/createPurchaseOrderSchema';

export type CreateFormValues = InferType<typeof createPurchaseOrderSchema>;

const DEFAULT_VALUES: Partial<CreateFormValues> = {
  deposit_event_offset: 0,
  deposit_event_offset_direction: 1,
  deposit_percentage: '0',
  dirt_level: DirtLevelEnum.VC,
  due_date_direction: 1,
  due_date_offset: 0,
  is_food_grade: false,
  is_internal_grade_shown_to_seller: true,
  is_long_term_contract: false,
  is_variable_pricing: false,
  seller_currency: Currency.GBP,
  should_update_material_properties_from_site_material: true,
  // We can't set the value from e2e tests so we set it to a default value
  target_collection_date: import.meta.env.VITE_CI ? dayjs().toISOString() : undefined,
  third_party_commission_currency: Currency.USD,
  third_party_commission_per_tonne: '0',
  tonnage_variance: '10',
  tonnes: '',
};

export const PurchaseOrderCreate = () => {
  const form = useForm<CreateFormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(createPurchaseOrderSchema),
  });
  const { data: user } = useApiAccountsV1UserRetrieve();

  const { mutate: createOrder, isPending } = useCreatePurchaseOrder(form);

  const onSubmit = async (values: CreateFormValues) => {
    if (!user) {
      throw new Error('User not found');
    }

    const dueDateOffset = (values.due_date_offset || 0) * values.due_date_direction;
    const depostEventOffset = (values.deposit_event_offset || 0) * values.deposit_event_offset_direction;
    const targetCollectionDate = dayjs(values.target_collection_date).format('YYYY-MM-DD');
    const fixingDate =
      values.who_fixes_date && values.fixing_date ? dayjs(values.fixing_date).format('YYYY-MM-DD') : undefined;
    const pricingType = values.is_variable_pricing ? PricingTypeEnum.INDEX : PricingTypeEnum.FIXED;

    const siteMaterialId = values.site_material_id;
    if (!siteMaterialId) {
      throw new Error('Site material id is required');
    }

    const isVariablePricing = pricingType === PricingTypeEnum.INDEX;

    const orderData: CreatePurchaseOrderDTO = {
      ...values,
      created_by_id: user.id,
      deposit_event_offset: depostEventOffset,
      due_date_offset: dueDateOffset,
      fixing_date: fixingDate,
      pricing_type: pricingType,
      site_material_id: siteMaterialId,
      target_collection_date: targetCollectionDate,
    };

    if (isVariablePricing) {
      orderData.preliminary_price = values.price_per_tonne;
    }

    if (!values.is_variable_pricing) {
      (
        [
          'index',
          'price_as_percentage_of_index',
          'cash_or_three_month',
          'who_fixes_date',
          'fixing_date',
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
          'price_point',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    if (values.who_fixes_date === WhoFixesDateEnum.CUSTOMER || values.who_fixes_date === WhoFixesDateEnum.SAFI) {
      orderData.fixing_date = undefined;
    } else {
      (
        [
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    createOrder({
      data: orderData,
    });
  };

  return (
    <Page>
      <Page.Title title="Create Purchase Order" subtitle="Please fill in the purchase order details." />
      <Page.Divider />
      <Section>
        <FormContextProvider defaultValues={undefined} variant="create" documents={[]} purchaseOrderState={undefined}>
          <Form {...form} onSubmit={onSubmit} devtool={false}>
            <PurchaseOrderFormSections />
            <Box display="flex" justifyContent="end" alignItems="center" paddingTop={4}>
              <Button type="submit" size="large" loading={isPending}>
                Submit
              </Button>
            </Box>
          </Form>
        </FormContextProvider>
      </Section>
    </Page>
  );
};
